import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import {
    useCategoryUrlGenerator,
    useFormatUrlGenerator,
    useTopicUrlGenerator,
} from '../../hooks/useUrlGenerator';
import { createLayoutRow, createVideosRow } from '../../lib/blocks';

import { useSite } from '../../contexts/SiteContext';
import UrbaniaHeader from '../headers/UrbaniaHeader';
import RowTitle from '../partials/RowTitle';
import HomePageBlocks, { HomePageBlocksProps } from './HomePageBlocks';

import styles from '../../styles/pages/home-page-france.module.css';

interface HomeSection {
    id: string;
    label: string;
    query: Record<string, unknown>;
    url?: string;
}

function HomePageFrance({ brand, className = null, ...props }: HomePageBlocksProps) {
    const url = useUrlGenerator();
    const formatUrl = useFormatUrlGenerator();
    const topicUrl = useTopicUrlGenerator();
    const categoryUrl = useCategoryUrlGenerator();
    const { handle: siteHandle } = useSite();

    const sections: HomeSection[] = [
        {
            id: 'actualites',
            label: 'Actualité',
            query: {
                section: 'actualites',
            },
            url: url('section', { section: 'actualites' }),
        },
        {
            id: 'culture',
            label: 'Culture',
            query: {
                section: 'culture',
            },
            url: url('section', { section: 'culture' }),
        },
        {
            id: 'societe',
            label: 'Société',
            query: {
                section: 'societe',
            },
            url: url('section', { section: 'societe' }),
        },
        {
            id: 'feminisme',
            label: 'Féminisme',
            query: {
                topic: 'feminisme',
            },
            url: topicUrl({ slug: 'feminisme' }),
        },
        {
            id: 'humour',
            label: 'Humour',
            query: {
                section: 'humour',
            },
            url: url('section', { section: 'humour' }),
        },
        {
            id: 'environnement',
            label: 'Environnement',
            query: {
                category: 'environnement',
            },
            url: categoryUrl({ slug: 'environnement' }),
        },
        {
            id: 'politique',
            label: 'Politique',
            query: {
                category: 'politique',
            },
            url: categoryUrl({ slug: 'politique' }),
        },
        {
            id: 'humains',
            label: 'Humains',
            query: {
                section: 'humains',
            },
            url: url('section', { section: 'humains' }),
        },
        {
            id: 'chronique',
            label: 'Chronique',
            query: {
                format: 'chronique',
            },
            url: formatUrl({ slug: 'chronique' }),
        },
    ];
    const blocks = [
        {
            role: 'block',
            type: 'header_featured_list',
            brand,
            cardTheme: 'over-bottom-left',
            cardWithout: ['category'],
            count: 1,
            className: styles.featuredBlock,
            innerClassName: styles.featuredInner,
            cardClassName: styles.featuredCard,
            query: {
                brand: brand.id,
                exclude_type: ['contest', 'newsletter'],
                placement: ['home_featured_france'],
            },
            uniqueDocuments: true,
        },
        createLayoutRow('1_1_1_4', {
            cardTheme: 'box-translucent',
            title: (
                <RowTitle
                    url={url('all', {
                        brand: brand.slug,
                    })}
                >
                    <FormattedMessage
                        defaultMessage="Les plus récents"
                        description="Section title"
                    />
                </RowTitle>
            ),
            query: {
                brand: brand.id,
                exclude_type: ['newsletter'],
                placement: ['home_featured_france'],
                // type: ['article', 'video'],
            },
            clickRef: 'home_new',
            loading: 'lazy',
            cardImageLoading: 'lazy',
            uniqueDocuments: true,
            withAds: true,
            adsTargeting: {
                placement: 'home_featured_france',
            },
        }),
        createLayoutRow('1_1_1_4', {
            cardTheme: 'box-translucent',
            title: (
                <RowTitle
                    url={url('popular', {
                        brand: brand.slug,
                    })}
                >
                    <FormattedMessage
                        defaultMessage="Les plus populaires"
                        description="Section title"
                    />
                </RowTitle>
            ),
            query: {
                brand: brand.id,
                placement: ['home_popular_france', 'home_featured_france'],
                // popular: 'week',
            },
            clickRef: 'home_popular',
            hook: 'popular',
            loading: 'lazy',
            cardImageLoading: 'lazy',
            uniqueDocuments: true,
        }),
        // createCollectionRow('fragments-2', {
        //     // summaryPosition: 'top',
        //     summaryImagePosition: 'inline',
        //     presentation: 'grid',
        //     // cardType: 'over',
        //     cardSize: 'big',
        //     // cardTheme: 'outline',
        //     cardRatio: 'horizontal',
        //     // cardTheme: 'box-translucent',
        //     clickRef: 'home_collection',
        //     cardWidth: 250,
        //     minColumns: 1,
        //     cardWithout: ['author'],
        //     withBackground: true,
        //     withoutSummaryImage: true,
        //     className: styles.gazaCollection,
        //     summaryTitleClassName: styles.summaryTitle,
        //     withBorderRadius: false,
        // }),
        {
            role: 'block',
            type: 'header_featured_sidebar',
            presentation: 'row',
            itemsType: 'documents',
            title: <RowTitle url={url('micromags')}>Micromag URBANIA</RowTitle>,
            query: {
                type: 'micromag',
                collection: `micromag-${siteHandle}`,
            },
            loading: 'lazy',
            cardImageLoading: 'lazy',
            featuredCardType: 'featured-micromag',
            featuredCardTheme: 'left',
            featuredCardRatio: 'micromag',
            featuredCardWith: ['image-link'],
            featuredCardWithout: ['link'],
            cardType: 'over',
            cardSize: 'big',
            cardTheme: 'outline',
            cardRatio: 'micromag',
            responsive: true,
            cardWidth: 200,
            // count: 9,
            cardWithout: ['text', 'type-icon'],
            withBorderRadius: false,
            featuredCardClassName: styles.featuredMicromag,
        },
        createVideosRow({
            title: (
                <RowTitle url={url('videos')}>
                    <FormattedMessage
                        defaultMessage="Vidéos récentes"
                        description="Section title"
                    />
                </RowTitle>
            ),
            query: {
                brand: brand.id,
            },
            clickRef: 'home_videos',
            loading: 'lazy',
            cardImageLoading: 'lazy',
            cardWith: ['brand-icon'],
            cardWithout: ['category'],
            uniqueDocuments: true,
        }),
        {
            type: 'collections-row',
            presentation: 'grid',
            title: (
                <RowTitle url={url('videos')}>
                    <FormattedMessage defaultMessage="Séries vidéos" description="Section title" />
                </RowTitle>
            ),
            count: 6,
            query: {
                // brand: brand.id,
                handle: [
                    '5-trucs',
                    'mic',
                    'darwins-awards',
                    'francis-recap',
                    'en-59-secondes',
                    'prof-apeupres',
                ],
            },
            loading: 'lazy',
            cardImageLoading: 'lazy',
            cardType: 'vertical',
            cardRatio: 'vertical',
            cardTheme: 'box-translucent',
            cardWidth: 200,
        },

        ...sections.map(({ id: sectionId, query, label, url: sectionUrl }) =>
            createLayoutRow('1_1_1_4', {
                role: 'block',
                title: <RowTitle url={sectionUrl}>{label}</RowTitle>,
                loading: 'lazy',
                query,
                clickRef: `home_section_${sectionId}`,
                cardType: 'vertical',
                cardTheme: 'box-translucent',
                cardWithout: ['category'],
                uniqueDocuments: true,
            }),
        ),

        {
            type: 'authors-row',
            presentation: 'grid',
            minColumns: 1,
            maxColumns: 4,
            className: styles.collectionsRow,
            title: <RowTitle>Dans l’univers URBANIA</RowTitle>,
            count: 8,
            query: {
                // brand: brand.id,
                handle: [
                    'ouissem',
                    'matthieu-beigbeder',
                    'mathieu-gilbert',
                    'malia-kounkou',
                    'oriane-olivier',
                    'louise-pierga',
                    'hanneli-victoire',
                    'benoit-lelievre',
                ],
                // for: 'documents',
                order: 'count',
            },
            loading: 'lazy',
            cardImageLoading: 'lazy',
            cardType: 'documents',
            cardRatio: 'square',
            cardSize: 'medium',
            cardTheme: 'box-translucent',
            cardWidth: 300,
            cardProps: {
                listProps: {
                    loading: 'lazy',
                    cardImageLoading: 'lazy',
                },
                listCount: 4,
            },
            cardWithout: ['text'],
        },
    ];
    return (
        <HomePageBlocks
            {...props}
            brand={brand}
            blocks={blocks}
            header={<UrbaniaHeader className={styles.header} />}
            className={classNames([styles.container, className])}
            blocksClassName={styles.blocks}
        />
    );
}

export default HomePageFrance;
